import * as React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { Edit2, Check } from "react-feather";
import { MenuItem } from "../../../../store/menuSlice";
import { FavoriteMenuItem } from "./FavoriteMenuItem";
import { MetricsFavoriteItem } from "./MetricsFavoriteItem";

interface FavoritesSectionProps {
  items: MenuItem[];
  favoriteIds: string[];
  onReorder: (oldIndex: number, newIndex: number) => void;
  onUnpin: (id: string) => void;
  isEditMode: boolean;
  onToggleEditMode: () => void;
}

export const FavoritesSection: React.FC<FavoritesSectionProps> = ({
  items,
  favoriteIds,
  onReorder,
  onUnpin,
  isEditMode,
  onToggleEditMode,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = favoriteIds.findIndex((id) => id === active.id);
      const newIndex = favoriteIds.findIndex((id) => id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  if (favoriteIds.length === 0) {
    return null;
  }

  return (
    <div className="favorites-section">
      <div className="favorites-section-header">
        <h2 className="favorites-section-title">Favorites</h2>
        <button
          className="favorites-section-edit-button"
          onClick={onToggleEditMode}
          aria-label={isEditMode ? "Done editing" : "Edit favorites"}
        >
          {isEditMode ? <Check size={16} /> : <Edit2 size={16} />}
        </button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext items={favoriteIds} strategy={verticalListSortingStrategy}>
          <div className="favorites-section-list">
            {favoriteIds.map((id) => {
              const item = items.find((item) => item.id === id);
              if (!item) return null;
              return <FavoriteMenuItem key={item.id} item={item} onUnpin={onUnpin} isEditMode={isEditMode} />;
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
